<template>
  <div>
    <es-loader
      v-if="loading"
      style="margin: 6.5rem"
    />
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <template v-else>
      <es-filter :defaultExpanded="filterExpanded">
        <template #filter-options>
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-2">
              <div class="field">
                <label class="label has-text-weight-bold">
                  Transaction ID :
                </label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="transactionIdInput"
                  >
                </div>
              </div>
            </div>
            <div class="column is-2">
              <div class="field">
                <label class="label has-text-weight-bold">
                  Batch ID :
                </label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="batchIdInput"
                  >
                </div>
              </div>
            </div>
            <div class="column is-2">
              <es-select
                label="Submission Type : "
                :multi="true"
                ref="selSubmissionType"
                value-tpl="${description}"
                @update:values="matchTypeIds = $event"
                :preSelected="matchTypeIdsSelected"
                :setValues="MATCHTYPES"
                keyValue="id"
                :clearOut="true"
              />
            </div>
            <div class="column is-2">
              <es-select
                label="Match Type :"
                v-bind:multi="true"
                v-on:update:values="programs = $event"
                ref="selPrograms"
                valueTpl="${description}"
                :preSelected="programs"
                :setValues="indexProgramsNonDCX"
                keyValue="code"
                v-bind:clearOut="true"
              />
            </div>
            <div class="column is-2">
              <es-select
                label="Status :"
                v-bind:multi="true"
                valueTpl="${description}"
                keyValue="description"
                v-on:update:values="statusInput = $event"
                ref="selStatuses"
                :preSelected="statusInput"
                :setValues="statuses"
                v-bind:clearOut="true"
              />
            </div>
          </div>
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-2">
              <es-select
                :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:DISTRICT_CODE`)} :`"
                v-bind:multi="true"
                v-bind:search="true"
                api="districts"
                v-on:update:values="districts = $event"
                ref="selDistricts"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                :preSelected="districtCodes"
                searchMinLength="2"
                keyValue="code"
              />
            </div>
            <div class="column is-2">
              <es-select
                :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_CODE`)} :`"
                v-bind:multi="true"
                v-bind:search="true"
                api="schools"
                :apiQueryParams="getDistrictParams"
                v-on:update:values="schools = $event"
                ref="selSchools"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                :subValueTplFn="(result) =>
                  `${result.districtLocation.name} (<i>${result.districtCode }</i>)`"
                :preSelected="schoolCodesPromises"
                searchMinLength="2"
                keyValue="code"
              />
            </div>
            <div class="column is-2">
              <es-select
                :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_YEAR`)} :`"
                v-bind:multi="true"
                valueTpl="${year}"
                keyValue="id"
                v-on:update:values="schoolYearInput = $event"
                ref="selYears"
                :preSelected="schoolYearInput"
                :setValues="years"
                v-bind:clearOut="true"
              />
            </div>
            <div class="column is-4">
              <div class="field no-border">
                <label class="label has-text-weight-bold">
                  Process Date Range :
                </label>
                <div class="control is-flex">
                  <input
                    class="input"
                    type="date"
                    v-model="startDateInput"
                  >
                  <span
                    class="is-flex flex-align--center"
                    style="padding: 0 0.5rem; font-size: 12px"
                  >
                    to
                  </span>
                  <input
                    class="input"
                    type="date"
                    v-model="endDateInput"
                  >
                </div>
              </div>
            </div>
            <div
              class="column is-2"
              style="margin-top: auto; margin-left: auto"
            >
              <div class="field is-grouped flex-justify--end">
                <div class="control is-flex flex-align--center">
                  <a @click="resetFilter">
                    Reset
                  </a>
                </div>
                <div class="control">
                  <button
                    class="button is-primary"
                    @click="filterData"
                    :disabled="disableFilter"
                    :title= "disableFilter
                      ? 'Transaction ID, Batch ID, or Start Date is required.' : null"
                  >
                    Filter Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-filter>
      <es-exception
        v-if="isStateUser && !startDate && !dmMatchTransId && !batchId"
        style="padding: 6.5rem" margin="0rem"
        exceptionMessage="Transaction ID, Batch ID, or Start Date must be entered to view results."
      />
      <es-loader
        v-else-if="loadingTransactions"
        style="margin: 6.5rem"
      />
      <es-exception
        v-else-if="!loadingTransactions && loadingTransactionError && errMsgStr"
        style="padding: 6.5rem" margin="0rem"
        :exceptionMessage = "errMsgStr"
      />
      <es-exception
        v-else-if="!loadingTransactions && loadingTransactionError"
        style="padding: 6.5rem" margin="0rem"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <es-exception
        v-else-if="!loadingTransactions && !loadingTransactionError && !transactions.length"
        style="padding: 6.5rem" margin="0rem"
        exceptionMessage="No results found."
      />
      <div
        v-else-if="!loadingTransactions && !loadingTransactionError && transactions.length"
        class="content"
      >
        <dm-transactions
          :apiSorter="apiSorter"
          :columnHeaders="columnHeaders"
          :totalCount="transactionsTotalCount"
          :transactionList="transactions"
          @updateTransactionColumnHeaders="updateSorter"
        />
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { mapActions, mapGetters } from 'vuex';
import DmTransactions from '@/components/DmTransactions.vue';
import EsException from '@/components/EsException.vue';
import EsFilter from '@/components/EsFilter.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';

export default {
  mixins: [
    DateUtils,
    FieldLabels,
  ],
  components: {
    DmTransactions,
    EsException,
    EsFilter,
    EsLoader,
    EsSelect,
  },
  props: {
    batchId: {
      type: String,
    },
    districtCodes: {
      type: Array,
    },
    dmMatchTransId: {
      type: String,
    },
    endDate: {
      type: String,
    },
    matchTypeId: {
      type: Array,
    },
    page: {
      type: String,
    },
    programCodes: {
      type: Array,
    },
    schoolCodes: {
      type: Array,
    },
    schoolYear: {
      type: Array,
    },
    status: {
      type: Array,
    },
    startDate: {
      type: String,
    },
    sortField: {
      type: String,
      default: 'id',
    },
    sortOrder: {
      type: String,
      default: 'DESC',
    },
  },
  data() {
    const STATUSES = [
      {
        status: 'COMPLETED',
        description: 'Completed',
        id: ['1', '4', '13', '14', '15', '17'],
      },
      {
        status: 'ERRORED',
        description: 'Errors',
        id: ['90'],
      },
      {
        status: 'ERRORED_VALIDATION',
        description: 'Interrupted',
        id: ['99'],
      },
      {
        status: 'COMPLETED_WITH_PENDING',
        description: 'Near Matches',
        // TODO: Add Near Match System Label
        id: ['2'],
      },
    ];
    return {
      // props and filter
      batchIdInput: this.batchId,
      districts: this.districtCodes.map(d => ({ code: d })),
      endDateInput: this.endDate,
      initialDate: null,
      matchTypeIds: [],
      MATCHTYPES: null,
      programs: [],
      schools: this.schoolCodes.map(code => ({ districtCode: code.split(/[:]+/)[1], code: code.split(/[:]+/)[0] })),
      schoolYearInput: null,
      startDateInput: this.startDate,
      statusInput: STATUSES.filter(s => this.status.find(status => status === s.status)),
      statuses: STATUSES,
      transactionIdInput: this.dmMatchTransId,
      years: [],

      // data for batch view
      apiSorter: {},
      columnHeaders: [
        {
          header: null,
          field: null,
          style: {
            width: '55px',
          },
        },
        {
          header: 'Transaction Info',
          title: 'Transaction Info',
          field: 'id',
          order: 'DESC',
          sortable: true,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Match Type',
          title: 'Match Type',
          field: null,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Location Info',
          title: 'Location Info',
          field: null,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Process Info',
          title: 'Process Info',
          field: null,
          style: {
            idth: '10%',
          },
        },
        {
          header: 'Batch ID',
          title: 'Batch ID',
          field: null,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Status Info',
          title: 'Status Info',
          field: null,
        },
        {
          header: 'Actions',
          title: 'Actions',
          field: null,
          style: {
            width: '1%',
          },
        },
      ],
      filterExpanded: true,
      loading: true,
      loadingError: null,
      loadingTransactions: false,
      loadingTransactionError: null,
      transactions: [],
      transactionsTotalCount: null,
      userPersonTypesCodes: null,
    };
  },
  async created() {
    if (this.startDate || this.dmMatchTransId || this.batchId) {
      this.loadTransactions();
    }
    try {
      await Promise.all([
        this.loadDmSchoolYears(),
        this.loadMatchTypes('transaction'),
        this.loadPersonTypes(),
        this.loadPrograms(),
      ]);
      this.programs = this.indexProgramsNonDCX
        .filter(({ code }) => this.programCodes.includes(code));
      this.years = this.dmSchoolYearsDesc;
      this.schoolYearInput = this.years.filter(y => this.schoolYear.find(sy => sy === y.year));
      this.matchTypeIds = this.matchTypeIdsSelected;
    } catch (e) {
      this.loadingError = true;
    } finally {
      this.loading = false;
    }
    this.initialDate = this.$route.query.startDate ? new Date(this.$route.query.startDate) : null;
    this.userPersonTypesCodes = this.userPersonTypes
      .map(item => item.code);
  },
  mounted() {
    this.$root.$on('refreshMatchHistory', this.loadTransactions);
  },
  destroyed() {
    this.$root.$off('refreshMatchHistory');
  },
  watch: {
    $route() {
      if (!this.isStateUser || this.startDate || this.dmMatchTransId || this.batchId) {
        this.loadTransactions();
      }
    },
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters({
      isStateUser: 'isStateUser',
    }),
    ...mapGetters('lookups', [
      'defaultPersonType',
      'dmSchoolYearsDesc',
      'indexProgramsNonDCX',
    ]),
    dateDifference() {
      if (!this.initialDate) {
        return 29;
      }
      // Get today's date
      const today = new Date();
      // Calculate the time difference in milliseconds
      const timeDifference = today - this.initialDate;
      // Convert time difference from milliseconds to days
      const dayDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      // Return the difference in days
      return dayDifference;
    },
    disableFilter() {
      return this.isStateUser
        && !this.startDateInput
        && !this.transactionIdInput
        && !this.batchIdInput;
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);

      return params;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    hasInputParams() {
      return this.transactionIdInput || this.batchIdInput || this.programs.length
        || this.districts.length || this.endDateInput || this.page || this.schools.length
        || this.startDateInput || this.schoolYearInput.length || this.statusInput.length
        || this.matchTypes.length;
    },
    hasPropIds() {
      return this.dmMatchTransId || this.batchId;
    },
    matchTypeIdsSelected() {
      return this.MATCHTYPES.filter(m => this.matchTypeId.includes(m.id));
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
    searchParams() {
      const params = {};
      if (this.transactionIdInput) {
        params.dmMatchTransId = this.transactionIdInput.trim();
      }
      if (this.batchIdInput) {
        params.batchId = this.batchIdInput.trim();
      }
      if (this.matchTypeIds.length) {
        params.matchTypeIds = this.matchTypeIds.map(m => m.id);
      }
      if (this.programs.length) {
        params.programCode = this.programs.map(p => p.code);
      }
      if (this.statusInput.length) {
        params.status = this.statusInput.map(s => s.status);
      }
      if (this.districts.length) {
        params.districtCode = this.districts.map(d => d.code);
      }
      if (this.schools.length) {
        params.schoolCode = this.schools.map(s => `${s.code}:${s.districtCode}`);
      }
      if (this.schoolYearInput.length) {
        params.schoolYear = this.schoolYearInput.map(y => y.year);
      }
      if (this.startDateInput) {
        params.startDate = this.startDateInput;
      }
      if (this.endDateInput) {
        params.endDate = this.endDateInput;
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    urlParams() {
      const params = {};

      params.page = this.page ? this.page : undefined;
      params.dmMatchTransId = this.dmMatchTransId ? this.dmMatchTransId : undefined;
      params.dmBatchId = this.batchId ? this.batchId : undefined;
      params.programCode = this.programCodes.length ? this.programCodes : undefined;
      params.districtCode = this.districtCodes.length ? this.districtCodes : undefined;
      params.schoolCode = this.schoolCodes.length ? this.getSchoolCodes() : undefined;
      params.schoolYear = this.schoolYear.length ? this.schoolYear : undefined;
      params.status = this.status.length
        ? this.statuses.filter(s => this.status.find(statusProp => statusProp === s.status))
          .map(o => o.id).flat()
        : undefined;
      params.matchTypeIds = this.matchTypeIds.length
        ? this.matchTypeIds.map(m => m.id) : undefined;
      params.startDate = this.startDate ? this.startDate : undefined;
      params.endDate = this.endDate ? this.endDate : undefined;
      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);

      return params;
    },
  },
  asyncComputed: {
    async schoolCodesPromises() {
      const promises = [];
      this.schoolCodes.forEach(code => promises
        .push(axios.get(`districts/${code.split(/[:]+/)[1]}/schools/${code.split(/[:]+/)[0]}`)));
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPrograms',
      'loadDmSchoolYears',
      'loadPersonTypes',
    ]),
    filterData() {
      if (!this.hasInputParams && this.searchParams) {
        this.resetFilter();
      } else {
        this.$router.replace({ query: this.searchParams });
      }
    },
    getSchoolCodes() {
      return this.schoolCodes.map(code => code.split(/[:]+/)[0]);
    },
    getTransactions() {
      return axios.get('directmatch/transactions',
        {
          params: this.urlParams,
          paramsSerializer(params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
    },
    async loadMatchTypes(type) {
      const filter = type ? `?filter=${type}` : '';
      const res = await axios.get(`matchtypes${filter}`);
      this.MATCHTYPES = res.data;
    },
    async loadTransactions() {
      try {
        this.$emit('isPageLoaded', false);
        this.loadingTransactions = true;
        this.loadingTransactionError = false;

        const response = await this.getTransactions();
        this.transactions = response.data.data;
        this.transactionsTotalCount = response.data.totalCount;
        [this.apiSorter] = response.data.sorters;
        this.$emit('isPageLoaded', true);
      } catch (e) {
        this.loadingTransactionError = true;
        this.errMsgStr = e.response.data;
      } finally {
        this.loadingTransactions = false;
      }
    },
    resetFilter() {
      // TODO: searchParams() returns all keys (even if the value is undefined)
      if (Object.keys(this.urlParams).length) {
        this.$router.replace({ query: {} });
      }

      this.$refs.selDistricts.clear();
      this.$refs.selSchools.clear();
      this.$refs.selPrograms.clear();
      this.$refs.selStatuses.clear();
      this.$refs.selYears.clear();
      this.$refs.selSubmissionType.clear();

      this.transactionIdInput = '';
      this.batchIdInput = '';
      this.startDateInput = '';
      this.endDateInput = '';

      this.transactions = [];
      this.transactionsTotalCount = null;
      this.$emit('isPageLoaded', false);

      // set year back to current year.
      // set start date to 30 days prior.
      this.$refs.selYears.pushValues(
        this.years.filter(y => this.$store.state.propertiesModule.properties['RULE-CURRENT_SCHOOL_YEAR'] === y.year),
      );
      this.startDateInput = this.buildDateString(this.dateDifference);
      this.filterData();
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
